import { useCallback, useEffect, useState } from 'react';
import type { Tenant } from '../../lib/database.types';
import { formatPrice } from '../../lib/cart';
import {
  listPromoCodes,
  createPromoCode,
  setPromoCodeActive,
  deletePromoCode,
  type PromoCodeWithUsage,
} from '../../lib/promoService';
import ConfirmDelete from './ConfirmDelete';

/**
 * Écran admin « Codes promo » (par resto). Objectif : créer / activer / désactiver
 * un code en < 1 min (codes rotatifs, ex. changer chaque semaine). Le code SMS
 * (Odoo) doit correspondre à un code ACTIF ici.
 */
export default function PromoCodesSection({ tenant }: { tenant: Tenant }) {
  const [codes, setCodes] = useState<PromoCodeWithUsage[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{ label: string; run: () => void } | null>(null);
  const cur = tenant.currency;

  // Formulaire de création.
  const [code, setCode] = useState('');
  const [type, setType] = useState<'percent' | 'amount'>('percent');
  const [value, setValue] = useState('20');
  const [minOrder, setMinOrder] = useState('');
  const [maxDiscount, setMaxDiscount] = useState('');
  const [firstOnly, setFirstOnly] = useState(true);
  const [endsAt, setEndsAt] = useState('');
  const [maxRedemptions, setMaxRedemptions] = useState('');
  const [formMsg, setFormMsg] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setCodes(await listPromoCodes(tenant.id));
    setLoading(false);
  }, [tenant.id]);
  useEffect(() => { reload(); }, [reload]);

  const run = (fn: () => Promise<unknown>) => async () => {
    setBusy(true);
    try { await fn(); await reload(); } finally { setBusy(false); }
  };

  const euros = (s: string) => (s.trim() ? Math.round(parseFloat(s.replace(',', '.')) * 100) : null);

  const submit = run(async () => {
    setFormMsg(null);
    const name = code.trim().toUpperCase();
    const v = type === 'percent' ? parseInt(value || '0', 10) : Math.round(parseFloat(value.replace(',', '.') || '0') * 100);
    if (!name || !v || v <= 0) { setFormMsg('Renseignez un code et une valeur > 0.'); return; }
    const created = await createPromoCode(tenant.id, {
      code: name,
      type,
      value: v,
      min_order_cents: euros(minOrder),
      max_discount_cents: type === 'percent' ? euros(maxDiscount) : null,
      first_order_only: firstOnly,
      ends_at: endsAt ? new Date(endsAt).toISOString() : null,
      max_redemptions: maxRedemptions.trim() ? parseInt(maxRedemptions, 10) : null,
      active: true,
    });
    if (!created) { setFormMsg('Création impossible (code déjà existant pour ce resto ?).'); return; }
    setCode(''); setValue(type === 'percent' ? '20' : '3'); setMinOrder(''); setMaxDiscount(''); setMaxRedemptions(''); setEndsAt('');
  });

  const describe = (c: PromoCodeWithUsage) => {
    const val = c.type === 'percent' ? `-${c.value}%` : `-${formatPrice(c.value, cur)}`;
    const bits = [val];
    if (c.min_order_cents) bits.push(`min. ${formatPrice(c.min_order_cents, cur)}`);
    if (c.max_discount_cents && c.type === 'percent') bits.push(`plafond ${formatPrice(c.max_discount_cents, cur)}`);
    if (c.first_order_only) bits.push('1re commande');
    if (c.max_redemptions != null) bits.push(`max ${c.max_redemptions}`);
    if (c.ends_at) bits.push(`jusqu'au ${new Date(c.ends_at).toLocaleDateString('fr-FR')}`);
    return bits.join(' · ');
  };

  return (
    <div className="w-full max-w-4xl">
      <h2 className="mb-1 text-xl font-black">🎟️ Codes promo</h2>
      <p className="mb-4 text-sm text-slate-500">
        Créez et activez/désactivez vos codes (rotatifs). Le code envoyé par SMS doit correspondre à un code <strong>actif</strong> ici.
      </p>

      {/* Création rapide */}
      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="mb-3 text-base font-bold">Nouveau code</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs font-semibold opacity-60">Code</label>
            <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="BURGER20" className="w-full rounded-lg border border-slate-200 p-2 text-sm uppercase" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold opacity-60">Type</label>
            <select value={type} onChange={(e) => setType(e.target.value as 'percent' | 'amount')} className="w-full rounded-lg border border-slate-200 p-2 text-sm">
              <option value="percent">Pourcentage (%)</option>
              <option value="amount">Montant fixe (€)</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold opacity-60">{type === 'percent' ? 'Réduction (%)' : 'Réduction (€)'}</label>
            <input value={value} onChange={(e) => setValue(e.target.value)} type="number" step={type === 'percent' ? '1' : '0.10'} className="w-full rounded-lg border border-slate-200 p-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold opacity-60">Min. commande (€)</label>
            <input value={minOrder} onChange={(e) => setMinOrder(e.target.value)} type="number" step="0.10" placeholder="—" className="w-full rounded-lg border border-slate-200 p-2 text-sm" />
          </div>
          {type === 'percent' && (
            <div>
              <label className="mb-1 block text-xs font-semibold opacity-60">Plafond réduction (€)</label>
              <input value={maxDiscount} onChange={(e) => setMaxDiscount(e.target.value)} type="number" step="0.10" placeholder="—" className="w-full rounded-lg border border-slate-200 p-2 text-sm" />
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs font-semibold opacity-60">Fin de validité</label>
            <input value={endsAt} onChange={(e) => setEndsAt(e.target.value)} type="date" className="w-full rounded-lg border border-slate-200 p-2 text-sm" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold opacity-60">Max utilisations (global)</label>
            <input value={maxRedemptions} onChange={(e) => setMaxRedemptions(e.target.value)} type="number" placeholder="illimité" className="w-full rounded-lg border border-slate-200 p-2 text-sm" />
          </div>
          <label className="flex items-end gap-2 pb-2 text-sm">
            <input type="checkbox" checked={firstOnly} onChange={(e) => setFirstOnly(e.target.checked)} />
            <span className="font-semibold">Réservé 1re commande en ligne</span>
          </label>
        </div>
        {formMsg && <p className="mt-2 text-sm text-red-600">{formMsg}</p>}
        <div className="mt-3">
          <button onClick={submit} disabled={busy} className="rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-40" style={{ background: 'var(--brand-primary)' }}>
            + Créer le code
          </button>
        </div>
      </div>

      {/* Liste */}
      {loading ? (
        <p className="opacity-70">Chargement…</p>
      ) : codes.length === 0 ? (
        <p className="text-sm opacity-60">Aucun code pour l'instant. Créez-en un ci-dessus.</p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Détail</th>
                <th className="px-3 py-2 text-center">Utilisé</th>
                <th className="px-3 py-2 text-center">Actif</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-mono font-bold">{c.code}</td>
                  <td className="px-3 py-2 text-slate-600">{describe(c)}</td>
                  <td className="px-3 py-2 text-center">{c.redemptions ?? 0}{c.max_redemptions != null ? ` / ${c.max_redemptions}` : ''}</td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={run(() => setPromoCodeActive(c.id, !c.active))}
                      disabled={busy}
                      className={'rounded-full px-2.5 py-1 text-xs font-bold ' + (c.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500')}
                    >
                      {c.active ? '✓ Actif' : 'Inactif'}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => setPendingDelete({ label: `le code « ${c.code} »`, run: run(() => deletePromoCode(c.id)) })}
                      className="text-slate-400 hover:text-red-600"
                      title="Supprimer"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDelete
        open={!!pendingDelete}
        label={pendingDelete?.label ?? ''}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => { pendingDelete?.run(); setPendingDelete(null); }}
      />
    </div>
  );
}
